// @view/panels/document/review/SectionCard.panel.tsx
import React from "react";
import type { Ok } from "@ps-bridge/ok";
import * as styles from "@styles/SectionCard.css";

export type SectionCardPhase = "active" | "inactive";
export type SectionCardContentPhase = "content" | "placeholder";

type SectionCardPanelProps = {
  title: string;
  contentHtml: string;
  phase: SectionCardPhase;
  contentPhase: SectionCardContentPhase;
  onPress: () => Ok;
};

const SectionCardPanel: React.FC<SectionCardPanelProps> = ({
  title,
  contentHtml,
  phase,
  contentPhase,
  onPress,
}) => {
  const isActive = phase === "active";
  const contentRender: Record<SectionCardContentPhase, React.ReactElement> = {
    content: (
      <div
        data-testid="review-section-markdown"
        className={styles.markdown}
        dangerouslySetInnerHTML={{
          __html: contentHtml,
        }}
      />
    ),
    placeholder: (
      <p className={styles.placeholderText}>
        This section hasn't been started, tap or click this text, then the
        button that appears to get started.
      </p>
    ),
  };

  const innerCard = (
    <div className={`${styles.card} ${isActive ? styles.activeCard : ""}`}>
      <div className={styles.title}>{title}</div>
      <div className={styles.contentCard}>{contentRender[contentPhase]}</div>
    </div>
  );

  const phaseView: Record<SectionCardPhase, React.ReactElement> = {
    active: <div>{innerCard}</div>,
    inactive: (
      <button
        className={styles.buttonWrapper}
        onClick={onPress}
        aria-label={`${title}`}
        aria-expanded={isActive}
      >
        {innerCard}
      </button>
    ),
  };

  return phaseView[phase];
};

export default React.memo(SectionCardPanel, (prevProps, nextProps) => {
  return (
    prevProps.phase === nextProps.phase &&
    prevProps.contentPhase === nextProps.contentPhase &&
    prevProps.title === nextProps.title &&
    prevProps.contentHtml === nextProps.contentHtml
  );
});
